import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Brain,
  Heart,
  Droplets,
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  Filter,
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { AdminHeadache, AdminTension, AdminLloro } from '../../types';

type RecordType = 'headaches' | 'tensions' | 'lloros';

export const AdminRecords: React.FC = () => {
  const [activeTab, setActiveTab] = useState<RecordType>('headaches');
  const [headaches, setHeadaches] = useState<AdminHeadache[]>([]);
  const [tensions, setTensions] = useState<AdminTension[]>([]);
  const [lloros, setLloros] = useState<AdminLloro[]>([]);
  const [pagination, setPagination] = useState({ limit: 20, offset: 0, total: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    intensity: '',
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    loadRecords();
  }, [activeTab, pagination.offset, filters]);

  const loadRecords = async () => {
    setIsLoading(true);
    try {
      const params = {
        limit: pagination.limit,
        offset: pagination.offset,
        search: searchTerm,
        ...filters,
      };

      if (activeTab === 'headaches') {
        const response = await adminAPI.getHeadaches(params);
        if (response.success) {
          setHeadaches(response.data.headaches);
          setPagination(prev => ({ ...prev, total: response.data.pagination.total }));
        }
      } else if (activeTab === 'tensions') {
        const response = await adminAPI.getTensions(params);
        if (response.success) {
          setTensions(response.data.tensions);
          setPagination(prev => ({ ...prev, total: response.data.pagination.total }));
        }
      } else {
        const response = await adminAPI.getLloros(params);
        if (response.success) {
          setLloros(response.data.lloros);
          setPagination(prev => ({ ...prev, total: response.data.pagination.total }));
        }
      }
    } catch (error) {
      console.error('Error loading records:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, offset: 0 }));
    loadRecords();
  };

  const handleExport = (format: 'csv' | 'json') => {
    let data: any[];
    let filename: string;

    if (activeTab === 'headaches') {
      data = headaches;
      filename = `headaches_export.${format}`;
    } else if (activeTab === 'tensions') {
      data = tensions;
      filename = `tensions_export.${format}`;
    } else {
      data = lloros;
      filename = `lloros_export.${format}`;
    }

    let content: string;
    let mimeType: string;

    if (format === 'json') {
      content = JSON.stringify(data, null, 2);
      mimeType = 'application/json';
    } else {
      const headers = Object.keys(data[0] || {}).join(',');
      const rows = data.map(row => Object.values(row).join(','));
      content = [headers, ...rows].join('\n');
      mimeType = 'text/csv';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getIntensityBadge = (intensity: string) => {
    const colors: Record<string, string> = {
      leve: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      moderada: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400',
      moderado: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400',
      fuerte: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${colors[intensity] || 'bg-gray-100'}`}>
        {intensity}
      </span>
    );
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  const tabs = [
    { id: 'headaches', label: 'Dolores de Cabeza', icon: Brain, color: 'text-orange-500' },
    { id: 'tensions', label: 'Tensiones', icon: Heart, color: 'text-red-500' },
    { id: 'lloros', label: 'Lloros', icon: Droplets, color: 'text-blue-500' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text)]">Registros</h1>
          <p className="text-[var(--color-text-muted)]">Todos los registros del sistema</p>
        </div>

        {/* Export */}
        <div className="flex gap-2">
          <button
            onClick={() => handleExport('csv')}
            className="btn btn-secondary"
          >
            <Download size={18} />
            CSV
          </button>
          <button
            onClick={() => handleExport('json')}
            className="btn btn-secondary"
          >
            <Download size={18} />
            JSON
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id as RecordType);
              setPagination(prev => ({ ...prev, offset: 0 }));
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-[var(--color-accent)] text-white'
                : 'bg-[var(--color-surface)] text-[var(--color-text-muted)] hover:bg-[var(--color-bg)]'
            }`}
          >
            <tab.icon size={18} className={activeTab === tab.id ? 'text-white' : tab.color} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
          <div className="relative w-full sm:flex-1 sm:min-w-[200px]">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Buscar por usuario..."
              className="input pl-10"
            />
          </div>

          {activeTab !== 'tensions' && (
            <select
              value={filters.intensity}
              onChange={(e) => setFilters(prev => ({ ...prev, intensity: e.target.value }))}
              className="input w-full sm:w-auto"
            >
              <option value="">Todas las intensidades</option>
              <option value="leve">Leve</option>
              <option value="moderada">Moderada</option>
              <option value="fuerte">Fuerte</option>
            </select>
          )}

          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
            className="input w-full sm:w-auto"
          />

          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
            className="input w-full sm:w-auto"
          />

          <button
            onClick={handleSearch}
            className="btn btn-primary w-full sm:w-auto"
          >
            <Filter size={18} />
            Filtrar
          </button>
        </div>
      </div>

      {/* Records Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg)]">
                <th className="text-left p-4 text-sm font-medium text-[var(--color-text-muted)]">Usuario</th>
                <th className="text-left p-4 text-sm font-medium text-[var(--color-text-muted)]">Fecha</th>
                {activeTab === 'headaches' && (
                  <>
                    <th className="text-left p-4 text-sm font-medium text-[var(--color-text-muted)]">Intensidad</th>
                    <th className="hidden md:table-cell text-left p-4 text-sm font-medium text-[var(--color-text-muted)]">Ubicación</th>
                    <th className="hidden sm:table-cell text-left p-4 text-sm font-medium text-[var(--color-text-muted)]">Medicación</th>
                  </>
                )}
                {activeTab === 'tensions' && (
                  <>
                    <th className="text-left p-4 text-sm font-medium text-[var(--color-text-muted)]">Sistólica</th>
                    <th className="text-left p-4 text-sm font-medium text-[var(--color-text-muted)]">Diastólica</th>
                    <th className="hidden sm:table-cell text-left p-4 text-sm font-medium text-[var(--color-text-muted)]">Pulso</th>
                  </>
                )}
                {activeTab === 'lloros' && (
                  <>
                    <th className="text-left p-4 text-sm font-medium text-[var(--color-text-muted)]">Intensidad</th>
                    <th className="hidden md:table-cell text-left p-4 text-sm font-medium text-[var(--color-text-muted)]">Duración</th>
                    <th className="hidden sm:table-cell text-left p-4 text-sm font-medium text-[var(--color-text-muted)]">Causa</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center">
                    <div className="w-8 h-8 border-3 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : (
                <>
                  {activeTab === 'headaches' && headaches.map((h) => (
                    <motion.tr
                      key={h.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg)] transition-colors"
                    >
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-[var(--color-text)]">{h.user_name}</p>
                          <p className="text-xs text-[var(--color-text-muted)]">{h.user_email}</p>
                        </div>
                      </td>
                      <td className="p-4 text-[var(--color-text-muted)]">{formatDate(h.date)}</td>
                      <td className="p-4">{getIntensityBadge(h.intensity)}</td>
                      <td className="hidden md:table-cell p-4 text-[var(--color-text-muted)]">{h.location || '-'}</td>
                      <td className="hidden sm:table-cell p-4 text-[var(--color-text-muted)]">
                        {h.medication_taken ? h.medication || 'Sí' : 'No'}
                      </td>
                    </motion.tr>
                  ))}

                  {activeTab === 'tensions' && tensions.map((t) => (
                    <motion.tr
                      key={t.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg)] transition-colors"
                    >
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-[var(--color-text)]">{t.user_name}</p>
                          <p className="text-xs text-[var(--color-text-muted)]">{t.user_email}</p>
                        </div>
                      </td>
                      <td className="p-4 text-[var(--color-text-muted)]">{formatDate(t.date)}</td>
                      <td className="p-4">
                        <span className="font-medium text-[var(--color-text)]">{t.systolic}</span>
                        <span className="text-[var(--color-text-muted)]"> mmHg</span>
                      </td>
                      <td className="p-4">
                        <span className="font-medium text-[var(--color-text)]">{t.diastolic}</span>
                        <span className="text-[var(--color-text-muted)]"> mmHg</span>
                      </td>
                      <td className="hidden sm:table-cell p-4 text-[var(--color-text-muted)]">{t.pulse || '-'} bpm</td>
                    </motion.tr>
                  ))}

                  {activeTab === 'lloros' && lloros.map((l) => (
                    <motion.tr
                      key={l.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg)] transition-colors"
                    >
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-[var(--color-text)]">{l.user_name}</p>
                          <p className="text-xs text-[var(--color-text-muted)]">{l.user_email}</p>
                        </div>
                      </td>
                      <td className="p-4 text-[var(--color-text-muted)]">{formatDate(l.date)}</td>
                      <td className="p-4">{getIntensityBadge(l.intensity)}</td>
                      <td className="hidden md:table-cell p-4 text-[var(--color-text-muted)]">{l.duration || '-'}</td>
                      <td className="hidden sm:table-cell p-4 text-[var(--color-text-muted)] max-w-[200px] truncate">
                        {l.trigger_cause || '-'}
                      </td>
                    </motion.tr>
                  ))}

                  {((activeTab === 'headaches' && headaches.length === 0) ||
                    (activeTab === 'tensions' && tensions.length === 0) ||
                    (activeTab === 'lloros' && lloros.length === 0)) && (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-[var(--color-text-muted)]">
                        No se encontraron registros
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-[var(--color-border)]">
            <p className="text-sm text-[var(--color-text-muted)]">
              Mostrando {pagination.offset + 1}-{Math.min(pagination.offset + pagination.limit, pagination.total)} de {pagination.total}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
                disabled={currentPage === 1}
                className="btn btn-secondary py-2 px-3 disabled:opacity-50"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                disabled={currentPage === totalPages}
                className="btn btn-secondary py-2 px-3 disabled:opacity-50"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
